import type { QrLoginInfoDto, QrLoginPollDto, QrLoginStartDto, SessionDto } from '@dijitalapp/shared';
import qrcode from 'qrcode-generator';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { api, ApiError, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { Icon } from './Icon';

/** A QR code as crisp SVG (one path), with a quiet zone. */
export function QrCode({ text, size = 220, label }: { text: string; size?: number; label: string }) {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  const q = 3;
  let d = '';
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + q} ${r + q}h1v1h-1z`;
  return (
    <svg className="qr" width={size} height={size} viewBox={`0 0 ${n + q * 2} ${n + q * 2}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect width="100%" height="100%" fill="#fff" />
      <path d={d} fill="#111418" />
    </svg>
  );
}

const MAX_REFRESHES = 4;

/**
 * Computer side: shows a short-lived QR code and waits for the phone to approve. Codes renew on their
 * own a few times, then pause until the user asks for a new one (no endless polling in a forgotten tab).
 */
export function QrLoginPanel({ onSession }: { onSession: (s: SessionDto) => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [pairing, setPairing] = useState<QrLoginStartDto | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'declined' | 'paused' | 'error'>('loading');
  const [round, setRound] = useState(0);
  const refreshes = useRef(0);
  const done = useRef(onSession);
  useEffect(() => {
    done.current = onSession;
  });

  useEffect(() => {
    let alive = true;
    let timer: number | undefined;
    void api<QrLoginStartDto>('POST', '/auth/qr/start').then(
      (p) => {
        if (!alive) return;
        setPairing(p);
        setState('ready');
        const tick = () => {
          void api<QrLoginPollDto>('POST', '/auth/qr/poll', { id: p.id, poll: p.poll }).then(
            (r) => {
              if (!alive) return;
              if (r.status === 'approved') return done.current(r.session);
              if (r.status === 'declined') return setState('declined');
              if (r.status === 'expired') {
                if (refreshes.current >= MAX_REFRESHES) return setState('paused');
                refreshes.current += 1;
                return setRound((n) => n + 1);
              }
              timer = window.setTimeout(tick, 2000);
            },
            () => alive && (timer = window.setTimeout(tick, 4000)),
          );
        };
        timer = window.setTimeout(tick, 2000);
      },
      () => alive && setState('error'),
    );
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [round]);

  const again = () => {
    refreshes.current = 0;
    setState('loading');
    setRound((n) => n + 1);
  };

  return (
    <div className="qr-login">
      <div className="qr-frame">
        {pairing && state === 'ready' ? (
          <QrCode text={pairing.url} label={t('qr.codeLabel')} />
        ) : (
          <div className="qr-placeholder">
            {state === 'loading' ? (
              <span className="muted">{t('app.loading')}</span>
            ) : (
              <>
                <p className="muted">{t(state === 'declined' ? 'qr.declined' : state === 'paused' ? 'qr.paused' : 'error.NETWORK')}</p>
                <button className="btn btn-primary btn-sm" type="button" onClick={again}>
                  {t('qr.newCode')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <ol className="qr-steps">
        <li>{t('qr.step1')}</li>
        <li>{t('qr.step2')}</li>
        <li>{t('qr.step3')}</li>
      </ol>
      <p className="hint qr-safety">
        <Icon name="shield" size={14} /> {t('qr.desktopSafety')}
      </p>
    </div>
  );
}

/** Phone side: /link#id.secret — shows what is asking to sign in, then approve or decline. */
export function LinkDevicePage() {
  const { i18n, config } = useApp();
  const { t, formatDate } = i18n;
  const navigate = useNavigate();
  const [parts] = useState(() => {
    const [id, secret] = window.location.hash.slice(1).split('.');
    return id && secret ? { id, secret } : null;
  });
  // The secret must not linger in history or in screenshots of the address bar.
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', '/link');
  }, []);
  const [info, setInfo] = useState<QrLoginInfoDto | null>(null);
  const [state, setState] = useState<'loading' | 'ask' | 'approved' | 'declined' | 'error'>(parts ? 'loading' : 'error');
  const [error, setError] = useState<string>(t('qr.invalid'));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!parts) return;
    void api<QrLoginInfoDto>('POST', '/auth/qr/inspect', parts).then(
      (i) => {
        setInfo(i);
        setState('ask');
      },
      (err: unknown) => {
        setError(t(err instanceof ApiError && err.code === 'QR_LOGIN_EXPIRED' ? 'qr.expired' : errorMessageKey(err)));
        setState('error');
      },
    );
  }, [parts, t]);

  const decide = (approve: boolean) => {
    if (!parts) return;
    setBusy(true);
    api('POST', '/auth/qr/decide', { ...parts, approve })
      .then(
        () => setState(approve ? 'approved' : 'declined'),
        (err: unknown) => {
          setError(t(err instanceof ApiError && err.code === 'QR_LOGIN_EXPIRED' ? 'qr.expired' : errorMessageKey(err)));
          setState('error');
        },
      )
      .finally(() => setBusy(false));
  };

  const device = [info?.browser, info?.platform].filter(Boolean).join(' · ') || t('qr.unknownDevice');
  return (
    <div className="content-inner content-narrow">
      <section className="card stack link-card">
        <div className="link-mark" aria-hidden>
          <Icon name={state === 'approved' ? 'shield' : 'monitor'} size={36} />
        </div>
        {state === 'loading' && <p className="muted">{t('app.loading')}</p>}
        {state === 'ask' && info && (
          <>
            <h1>{t('qr.askTitle', { brand: config?.brand.name ?? 'DijitalApp' })}</h1>
            <dl className="detail-list">
              <div>
                <dt>{t('qr.device')}</dt>
                <dd>{device}</dd>
              </div>
              {info.ip && (
                <div>
                  <dt>{t('qr.address')}</dt>
                  <dd>{info.ip}</dd>
                </div>
              )}
              <div>
                <dt>{t('qr.when')}</dt>
                <dd>{formatDate(info.createdAt, { timeStyle: 'short' })}</dd>
              </div>
            </dl>
            <p className="alert alert-info" role="note">
              {t('qr.phoneSafety')}
            </p>
            <div className="row link-actions">
              <button className="btn" type="button" disabled={busy} onClick={() => decide(false)}>
                {t('qr.decline')}
              </button>
              <button className="btn btn-primary" type="button" disabled={busy} onClick={() => decide(true)}>
                {t('qr.approve')}
              </button>
            </div>
          </>
        )}
        {state === 'approved' && (
          <>
            <h1>{t('qr.approvedTitle')}</h1>
            <p className="muted">{t('qr.approvedBody')}</p>
            <NavLink className="btn" to="/settings/devices">
              {t('qr.seeDevices')}
            </NavLink>
          </>
        )}
        {state === 'declined' && (
          <>
            <h1>{t('qr.declinedTitle')}</h1>
            <button className="btn" type="button" onClick={() => navigate('/')}>
              {t('common.close')}
            </button>
          </>
        )}
        {state === 'error' && (
          <>
            <h1>{t('qr.errorTitle')}</h1>
            <p className="muted">{error}</p>
            <ScanButton />
          </>
        )}
      </section>
    </div>
  );
}

interface Detector {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
const detectorCtor = () =>
  (window as unknown as { BarcodeDetector?: new (o: { formats: string[] }) => Detector }).BarcodeDetector;

/** Opens the in-app camera scanner (where the browser can read QR codes). */
export function ScanButton() {
  const { i18n } = useApp();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn btn-primary" type="button" onClick={() => setOpen(true)}>
        <Icon name="monitor" size={18} /> {i18n.t('qr.scan')}
      </button>
      {open && <QrScanner onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Camera scanner for /link codes. Uses the browser's BarcodeDetector; where it is missing (iOS Safari)
 * the phone's own camera app reads the code and opens the link just the same.
 */
function QrScanner({ onClose }: { onClose: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const navigate = useNavigate();
  const video = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState<string | null>(() => (detectorCtor() && navigator.mediaDevices ? null : t('qr.noScanner')));

  useEffect(() => {
    const Ctor = detectorCtor();
    if (!Ctor || !navigator.mediaDevices) return;
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let alive = true;
    const detector = new Ctor({ formats: ['qr_code'] });
    void navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false }).then(
      (s) => {
        if (!alive) return s.getTracks().forEach((tr) => tr.stop());
        stream = s;
        if (video.current) {
          video.current.srcObject = s;
          void video.current.play();
        }
        const scan = async () => {
          if (!alive || !video.current) return;
          try {
            for (const code of await detector.detect(video.current)) {
              const url = new URL(code.rawValue, window.location.origin);
              if (url.origin === window.location.origin && url.pathname === '/link' && url.hash.length > 1) {
                onClose();
                navigate(`/link${url.hash}`);
                return;
              }
            }
          } catch {
            /* frame not ready yet */
          }
          timer = window.setTimeout(() => void scan(), 300);
        };
        void scan();
      },
      () => alive && setProblem(t('qr.cameraDenied')),
    );
    return () => {
      alive = false;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((tr) => tr.stop());
    };
  }, [navigate, onClose, t]);

  return (
    <div className="scanner" role="dialog" aria-modal="true" aria-label={t('qr.scan')}>
      <div className="scanner-box">
        {problem ? <p className="scanner-problem">{problem}</p> : <video ref={video} playsInline muted />}
        {!problem && <span className="scanner-target" aria-hidden />}
      </div>
      <p className="scanner-hint">{t('qr.scanHint')}</p>
      <button className="btn" type="button" onClick={onClose}>
        {t('common.close')}
      </button>
    </div>
  );
}

/** Settings > Devices: link a computer by scanning its QR code. */
export function LinkComputerCard() {
  const { i18n, me } = useApp();
  const { t } = i18n;
  if (!me) return null;
  return (
    <section className="card stack" aria-labelledby="link-computer">
      <div className="install-head">
        <span className="link-mark link-mark-sm" aria-hidden>
          <Icon name="monitor" size={24} />
        </span>
        <div>
          <h2 id="link-computer" className="card-title">
            {t('qr.linkTitle')}
          </h2>
          <p className="muted">{t('qr.linkBody')}</p>
        </div>
      </div>
      <div className="row">
        <ScanButton />
      </div>
    </section>
  );
}

/** Register page (computers): continue on the phone by scanning. */
export function OpenOnPhone() {
  const { i18n } = useApp();
  const { t } = i18n;
  return (
    <aside className="open-on-phone">
      <QrCode text={`${window.location.origin}/register`} size={112} label={t('qr.openOnPhone')} />
      <div>
        <strong>{t('qr.openOnPhone')}</strong>
        <p className="muted">{t('qr.openOnPhoneBody')}</p>
      </div>
    </aside>
  );
}
